import Joi from 'joi';
import { IntlFormatters } from 'react-intl';

import { emailRegEx, firstAndLastNameRegEx, postalCodeRegEx, cityRegEx, phoneRegEx} from '@marketplace/common';

import { TStatesCode, TAPICountry } from '@marketplace/api';

type TValidationSchemeOptions = {
  formatMessage: IntlFormatters['formatMessage'];
  countries: TAPICountry[];
};

export const registrationFormValidationSchema = ({ formatMessage, countries }: TValidationSchemeOptions) => {
  const emailLabel = formatMessage({ id: 'registrationContactInformation_email' });
  const firstNameLabel = formatMessage({ id: 'registrationContactInformation_firstName' });
  const lastNameLabel = formatMessage({ id: 'registrationContactInformation_lastName' });
  const dialCountryIdLabel = formatMessage({ id: 'registrationLocation_country' });
  const phoneLabel = formatMessage({ id: 'registrationContactInformation_phone' });
  const companyNameLabel = formatMessage({ id: 'registrationLocation_companyName',});
  const addressLabel = formatMessage({ id: 'registrationLocation_address' });
  const countryLabel = formatMessage({ id: 'registrationLocation_country' });
  const stateLabel = formatMessage({ id: 'registrationLocation_state' });
  const cityLabel = formatMessage({ id: 'registrationLocation_city' });
  const postalCodeLabel = formatMessage({ id: 'registrationLocation_postalCode',});
  const blockedDomain = ['fhirdevplatformstagingalt.aws.greenwayhealth.com','devplatform.greenwayhealth.com'];
  const DontAllowOtherDomain = ['localhost:3380','fhirdevplatformdevalt.aws.greenwayhealth.com','fhirdevplatformqaalt.aws.greenwayhealth.com'];

  const countriesWithStates = countries.filter(
    (country: any) => Object.values(TStatesCode).indexOf(country.key) >= 0
  );

  const stateValidationRules = countriesWithStates.map((country) => {
    return {
      is: Joi.object(country),
      then: Joi.object()
        .required()
        .messages({
          'any.required': formatMessage(
            { id: 'generalForm_validation_required' },
            { field: stateLabel }
          ),
        }),
    };
  });

  return Joi.object({
    email: Joi.string().trim().required().max(100).pattern(emailRegEx).custom((value, helpers) => {
      //For Staging & Prod
      if (blockedDomain.includes(document.location.host) && value.endsWith('@greenwayhealth.com')) {
        return helpers.error('string.domain.restricted', { domain: '@greenwayhealth.com' });
      }
      //For Dev & QA
      if (DontAllowOtherDomain.includes(document.location.host) && !value.endsWith('@greenwayhealth.com')) {
        return helpers.error('string.domain.allowed', { domain: '@greenwayhealth.com' });
      }
      return value;
    }).messages({
      'string.pattern.base': formatMessage({ id: 'generalForm_validation_email' }, { field: emailLabel }),
      'string.empty': formatMessage({ id: 'generalForm_validation_required' }, { field: emailLabel }),
      'string.max': formatMessage({ id: 'generalForm_validation_range' }, { field: emailLabel, min: 1, max: 100 }),
      'string.domain.restricted': formatMessage({ id: 'registrationFormGWHUserMessage' }, { domain: '@greenwayhealth.com' }),
      'string.domain.allowed': formatMessage({ id: 'registrationFormGWHUserAllowedMessage' }, { domain: '@greenwayhealth.com' }),
    }),
    // eslint-disable-next-line
    firstName: Joi.string().trim().required().max(100).pattern(firstAndLastNameRegEx).messages({
      'string.empty': formatMessage({ id: 'generalForm_validation_required' }, { field: firstNameLabel }),
      'string.max': formatMessage({ id: 'generalForm_validation_range' }, { field: firstNameLabel, min: 1, max: 100 }),
      'string.pattern.base': formatMessage({id:'generalForm_validation_regEx'},{field: firstNameLabel, RegEx: "alphabetical"})
    }),
    // eslint-disable-next-line
    lastName: Joi.string().trim().required().max(100).pattern(firstAndLastNameRegEx).messages({
      'string.empty': formatMessage({ id: 'generalForm_validation_required' }, { field: lastNameLabel }),
      'string.max': formatMessage({ id: 'generalForm_validation_range' }, { field: lastNameLabel, min: 1, max: 100 }),
      'string.pattern.base': formatMessage({id:'generalForm_validation_regEx'},{field: lastNameLabel, RegEx: "alphabetical"})
    }),
    dial_country_id: Joi.object().required().messages({
      'any.required': formatMessage({ id: 'generalForm_validation_required' }, { field: dialCountryIdLabel }),
    }),
    phone: Joi.string().trim().required().pattern(phoneRegEx).min(10).messages({
      'string.empty': formatMessage({ id: 'generalForm_validation_required' }, { field: phoneLabel }),
      'string.pattern.base': formatMessage({ id: 'generalForm_validation_regEx' }, { field: phoneLabel, RegEx: "numeric" }),
      'string.min': formatMessage({ id: 'registrationForm_phone_validation' }, { field: phoneLabel, character:10}),
    }),
    companyName: Joi.string().trim().max(100).messages({
        'string.max': formatMessage({ id: 'generalForm_validation_range' }, { field: companyNameLabel, min: 1, max: 100 }),
    }).allow(''),
    address: Joi.string().trim().max(2000).messages({
        'string.empty': formatMessage({ id: 'generalForm_validation_required' }, { field: addressLabel }),
        'string.max': formatMessage({ id: 'generalForm_validation_range' }, { field: addressLabel, min: 0, max: 2000 }),
    }).allow(''),
    country: Joi.object().required().messages({
        'any.required': formatMessage({ id: 'generalForm_validation_required' }, { field: countryLabel }),
    }),
    state: stateValidationRules?.length? Joi.object().when('country', {
          switch: stateValidationRules,
          otherwise: Joi.object().allow(''),
    }): Joi.object().allow(''),
    // eslint-disable-next-line
    city: Joi.string().trim().required().max(100).pattern(cityRegEx).messages({
        'string.empty': formatMessage({ id: 'generalForm_validation_required' }, { field: cityLabel }),
        'string.max': formatMessage({ id: 'generalForm_validation_range' }, { field: cityLabel, min: 1, max: 100 }),
        'string.pattern.base': formatMessage({id:'generalForm_validation_regEx'},{field: cityLabel, RegEx: "alphanumeric and (-)"})
    }),
    // eslint-disable-next-line
    postalCode: Joi.string().trim().max(20).pattern(postalCodeRegEx).messages({
        'string.max': formatMessage({ id: 'generalForm_validation_range' }, { field: postalCodeLabel, min: 1, max: 20 }),
        'string.pattern.base': formatMessage({id:'generalForm_validation_regEx'},{field: postalCodeLabel, RegEx: "alphanumeric"})
    }).allow(''),
    agree: Joi.boolean().invalid(false).required().messages({
        'any.invalid': formatMessage({ id: 'generalForm_validation_required' }, { field: '' }),
    }),
  });
}